import {
  atom,
  selector,
  SerializableParam,
  RecoilValueReadOnly,
  selectorFamily,
  atomFamily,
  useRecoilValue,
  useRecoilCallback,
  waitForAll,
} from "recoil";
import ApolloClient from "apollo-client";
import { MockLink } from "@apollo/react-testing";
import Auth, { CognitoUser } from "@aws-amplify/auth";
import { Hub, HubCallback } from "@aws-amplify/core";
import { defaultDataIdFromObject, InMemoryCache } from "apollo-cache-inmemory";
import { createHttpLink } from "apollo-link-http";
import { createAppSyncLink } from "aws-appsync";
import { Profile } from "./types";
import { v4 as uuid } from "uuid";

// @ts-ignore Ignore missing types
import awsconfig from "../aws-exports";
import { AppContextType } from "./AppContext";
import mixpanel from "mixpanel-browser";
import { DocumentNode } from "graphql";
import { isEqual } from "lodash";
import { useEffect } from "react";
import { DEMO_EMAIL } from "./useDemoMode";
import { mocks as selfGuidedMocks } from "./selfGuidedMocks";
import { localStorageEffect } from "./localStorageEffect";

const authConfigured = new Promise((resolve) => {
  const listener: HubCallback = (data) => {
    if (data.payload.event === "configured") {
      resolve();
      Hub.remove("auth", listener);
    }
  };

  Hub.listen("auth", listener);
});

const SELF_GUIDED_STORAGE_KEY = "selfGuidedMode";
/**
 * Whether the self guided tour option has been selected by the user.
 * When this value is true, it does not mean that the self guided tour is
 * being used. If the user has authenticated, then self-guided tour mode is disabled.
 * Use selfGuidedModeState to see if it's in effect
 */
export const selfGuidedTourSelectedState = atom({
  key: "SelfGuidedSelected",
  default: false,
  effects_UNSTABLE: [
    localStorageEffect({
      key: SELF_GUIDED_STORAGE_KEY,
      marshal: (newValue) => (newValue ? "true" : null),
      unmarshal: (value) => (value ? true : false),
    }),
  ],
});

const authConfiguredState = atom({
  key: "AuthConfigured",
  default: authConfigured,
});

export const cognitoUserRefreshState = atom<number>({
  key: "CognitoUserRefresh",
  default: 0,
});

const getCurrentAuthenticatedUser = async () => {
  try {
    const user: CognitoUser = await Auth.currentAuthenticatedUser();
    mixpanel.track("AuthenticatedNavigator - Authenticated");
    return user;
  } catch (e) {
    console.error(e);
    mixpanel.track("AuthenticatedNavigator - Unauthenticated", {
      error: e,
    });
    return null;
  }
};

export const cognitoUserState = selector<CognitoUser | null>({
  key: "CognitoUser",
  get: ({ get }) => {
    get(authConfiguredState);
    // Depend on the refresh atom
    get(cognitoUserRefreshState);

    return getCurrentAuthenticatedUser();
  },
  dangerouslyAllowMutability: true, // Cognito objects cannot be frozen
});

export const selfGuidedModeState = selector({
  key: "SelfGuidedTourMode",
  get: ({ get }) => {
    const [authData, selfGuidedTourSelected] = get(
      waitForAll([cognitoUserState, selfGuidedTourSelectedState])
    );
    return !authData && selfGuidedTourSelected;
  },
});

export const appContextState = selector<AppContextType | null>({
  key: "AppContext",
  get: ({ get }) => {
    // Rebuild when the Cognito user changes
    const selfGuidedMode = get(selfGuidedModeState);

    if (selfGuidedMode) {
      return {
        username: "demo",
        email: DEMO_EMAIL,
        isSuperuser: false,
      };
    }

    const authData = get(cognitoUserState);
    if (!authData) {
      return null;
    }

    const authPayload = authData
      .getSignInUserSession()!
      .getIdToken()
      .decodePayload();
    const userGroups: string[] = (authPayload["cognito:groups"] ||
      []) as string[];
    const appContext: AppContextType = {
      username: authData.getUsername(),
      email: authPayload["email"],
      isSuperuser: userGroups.includes("Superusers"),
    };

    mixpanel.identify(authData.getUsername());
    mixpanel.people.set("$email", authPayload["email"]);

    return appContext;
  },
});

export const selfGuidedClientState = selector<ApolloClient<{}>>({
  key: "ApolloClient-Mocked",
  get: () => {
    return new ApolloClient({
      link: new MockLink(selfGuidedMocks, true),
      cache: new InMemoryCache({
        addTypename: true,
        freezeResults: true,
        dataIdFromObject: (value) => {
          switch (value.__typename) {
            case "Profile":
              return (value as Profile).username;
            default:
              return defaultDataIdFromObject(value);
          }
        },
      }),
    });
  },
  dangerouslyAllowMutability: true,
});

export const authedClientState = selector<ApolloClient<{}> | null>({
  key: "ApolloClient-Authed",
  get: ({ get }) => {
    // Rebuild when the Cognito user changes
    const cognitoUser = get(cognitoUserState);
    if (!cognitoUser) return null;

    const httpLink = createHttpLink({
      uri: awsconfig.aws_appsync_graphqlEndpoint,
    });

    const awsLink = createAppSyncLink({
      url: awsconfig.aws_appsync_graphqlEndpoint,
      region: awsconfig.aws_appsync_region,
      auth: {
        type: "AMAZON_COGNITO_USER_POOLS",
        jwtToken: async () =>
          (await Auth.currentSession()).getIdToken().getJwtToken(),
        // FAKE_TOKEN
      },
      complexObjectsCredentials: () => Auth.currentCredentials(),
    });

    return new ApolloClient({
      link: awsLink.concat(httpLink),
      cache: new InMemoryCache({
        freezeResults: true,
        dataIdFromObject: (value) => {
          switch (value.__typename) {
            case "Profile":
              return (value as Profile).username;
            default:
              return defaultDataIdFromObject(value);
          }
        },
      }),
    });
  },
  dangerouslyAllowMutability: true,
});

export const clientState = selector<ApolloClient<unknown> | null>({
  key: "ApolloClient",
  get: ({ get }) => {
    const authClient = get(authedClientState);
    if (authClient) return authClient;

    const selfGuidedMode = get(selfGuidedModeState);
    if (selfGuidedMode) return get(selfGuidedClientState);

    return null;
  },
  dangerouslyAllowMutability: true,
});

export const queryStates = <
  V extends Readonly<{ [key: string]: SerializableParam }>,
  D
>(
  query: DocumentNode
): [(param: V) => RecoilValueReadOnly<D>, (param: V) => void] => {
  const queryId = uuid();
  const queryCacheState = atomFamily<D | undefined, V>({
    key: `ApolloQueryCache-${queryId}`,
    default: () => undefined,
  });

  const queryRefreshState = atomFamily<number, V>({
    key: `ApolloQueryRefresh-${queryId}`,
    default: 0,
  });

  const queryState = selectorFamily<D, V>({
    key: `ApolloQueryCache-${queryId}`,
    get:
      (data) =>
      ({ get }) => {
        get(queryRefreshState(data));
        const client = get(clientState);
        if (!client) throw new Error("No client available");

        const cached = get(queryCacheState(data));
        if (cached !== undefined) {
          return cached;
        }

        const variables = data;

        // Avoid using `await` so that this `get` method can avoid being a Promise
        // if possible
        return client
          .query<D, V>({
            query,
            variables,
          })
          .then((result) => {
            if (result.errors) {
              throw result.errors[0];
            }

            return result.data;
          });
      },
  });

  const useQueryWatch = (variables: V) => {
    const client = useRecoilValue(clientState);
    const callback = useRecoilCallback(
      ({ set }) =>
        () => {
          if (!client) return;
          const cancel = client.cache.watch({
            optimistic: true,
            query,
            variables,
            callback: (data) => {
              if (data.complete) {
                set(queryCacheState(variables), (oldValue) =>
                  isEqual(data.result, oldValue) ? oldValue : data.result
                );
              }
            },
          });

          return () => cancel();
        },
      [variables, client]
    );
    useEffect(callback, [callback, client, variables]);
  };

  return [queryState, useQueryWatch];
};

export const queryState = <
  V extends Readonly<{ [key: string]: SerializableParam }>,
  D
>(
  query: DocumentNode,
  variables: V
): [RecoilValueReadOnly<D>, () => void] => {
  const [state, watch] = queryStates<V, D>(query);
  return [state(variables), () => watch(variables)];
};

export const forceLogin = () => Auth.federatedSignIn();
